<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import TargetDropdown from './TargetDropdown.vue';

// Edits a single screen (the greeting root, or a reply screen). All mutations
// are emitted to the parent so it can keep the label==title link invariant.
// Day text and night text are shown TOGETHER (night is optional) so nothing is
// hidden behind the day/night toggle — the toggle only drives the preview.
const props = defineProps({
  screen: { type: Object, required: true },
  screens: { type: Array, default: () => [] },
});
const emit = defineEmits([
  'field',
  'btnLabel',
  'btnTarget',
  'btnNew',
  'btnAdd',
  'btnRemove',
  'removeScreen',
]);
const { t } = useI18n();

const MAX_TITLE = 20;
const MAX_BODY = 640;
const MAX_BUTTONS = 3;

const isRoot = computed(() => Boolean(props.screen.isRoot));
const buttons = computed(() => props.screen.buttons || []);
const replyText = computed(() => props.screen.text || '');
const offHoursText = computed(() => props.screen.offHours || '');
const hasReplyScreens = computed(
  () => props.screens.filter(s => !s.isRoot && s.title.trim()).length > 0
);
const entryButtonsEmpty = computed(
  () => buttons.value.filter(b => (b.label || '').trim()).length === 0
);
const greetingTextEmpty = computed(() => !(props.screen.text || '').trim());

// Target options exclude the greeting (it can't be reached via a button flow)
// and the current screen itself.
const targetOptions = computed(() =>
  props.screens
    .filter(s => !s.isRoot && s.id !== props.screen.id)
    .map(s => ({ id: s.id, title: s.title }))
);
</script>

<template>
  <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-5">
    <!-- header -->
    <div
      class="mb-4 flex items-center justify-between gap-3 border-b border-n-weak/60 pb-4"
    >
      <div class="flex items-center gap-3">
        <div
          class="grid size-9 place-items-center rounded-xl bg-n-brand/10 text-n-brand"
        >
          <span
            :class="isRoot ? 'i-lucide-house' : 'i-lucide-message-square-text'"
            class="size-[18px]"
          />
        </div>
        <div>
          <h2 class="text-base font-extrabold text-n-slate-12">
            {{
              isRoot
                ? t('AUTO_REPLY_BOT_SETTINGS.EDITOR.GREETING_TITLE')
                : t('AUTO_REPLY_BOT_SETTINGS.EDITOR.SCREEN_TITLE')
            }}
          </h2>
          <p class="mt-0.5 text-xs font-medium text-n-slate-10">
            {{
              isRoot
                ? t('AUTO_REPLY_BOT_SETTINGS.EDITOR.GREETING_SUB')
                : t('AUTO_REPLY_BOT_SETTINGS.EDITOR.SCREEN_SUB')
            }}
          </p>
        </div>
      </div>
      <button
        v-if="!isRoot"
        v-tooltip="t('AUTO_REPLY_BOT_SETTINGS.EDITOR.DELETE_SCREEN')"
        type="button"
        class="grid size-8 place-items-center rounded-lg border border-n-weak text-n-slate-10 transition-colors hover:border-n-ruby-6 hover:bg-n-ruby-3 hover:text-n-ruby-11"
        @click="emit('removeScreen')"
      >
        <span class="i-lucide-trash-2 size-4" />
      </button>
    </div>

    <!-- screen name (= the button text that opens it) -->
    <div v-if="!isRoot" class="mb-4">
      <div class="mb-1.5 flex items-center justify-between">
        <label class="text-[12.5px] font-bold text-n-slate-12">
          {{ t('AUTO_REPLY_BOT_SETTINGS.EDITOR.NAME_LABEL') }}
        </label>
        <span
          dir="ltr"
          class="text-[11px] font-semibold"
          :class="
            screen.title.length > MAX_TITLE
              ? 'text-n-ruby-11'
              : 'text-n-slate-10'
          "
        >
          {{ `${screen.title.length}/${MAX_TITLE}` }}
        </span>
      </div>
      <p class="mb-2 text-[11.5px] font-medium text-n-slate-10">
        {{ t('AUTO_REPLY_BOT_SETTINGS.EDITOR.NAME_HINT') }}
      </p>
      <input
        :value="screen.title"
        type="text"
        :maxlength="MAX_TITLE"
        :placeholder="t('AUTO_REPLY_BOT_SETTINGS.EDITOR.NAME_PLACEHOLDER')"
        class="w-full rounded-xl border border-n-weak bg-n-alpha-black1 px-3 py-2.5 text-sm font-semibold text-n-slate-12 outline-none transition-colors focus:border-n-brand"
        @input="emit('field', 'title', $event.target.value)"
      />
      <p
        v-if="!screen.title.trim()"
        class="mt-1.5 flex items-center gap-1.5 text-[11.5px] font-semibold text-n-amber-11"
      >
        <span class="i-lucide-triangle-alert size-3.5" />
        {{ t('AUTO_REPLY_BOT_SETTINGS.EDITOR.UNNAMED_HINT') }}
      </p>
    </div>

    <!-- day text -->
    <div class="mb-4">
      <div class="mb-1.5 flex items-center justify-between">
        <label class="text-[12.5px] font-bold text-n-slate-12">
          {{
            isRoot
              ? t('AUTO_REPLY_BOT_SETTINGS.EDITOR.GREETING_DAY_LABEL')
              : t('AUTO_REPLY_BOT_SETTINGS.EDITOR.DAY_BODY_LABEL')
          }}
        </label>
        <span
          dir="ltr"
          class="text-[11px] font-semibold"
          :class="
            replyText.length > MAX_BODY ? 'text-n-ruby-11' : 'text-n-slate-10'
          "
        >
          {{ `${replyText.length}/${MAX_BODY}` }}
        </span>
      </div>
      <textarea
        :value="screen.text"
        :rows="isRoot ? 4 : 5"
        :placeholder="
          t('AUTO_REPLY_BOT_SETTINGS.EDITOR.REPLY_TEXT_PLACEHOLDER')
        "
        class="w-full resize-y rounded-xl border bg-n-alpha-black1 px-3 py-2.5 text-sm leading-relaxed text-n-slate-12 outline-none transition-colors focus:border-n-brand"
        :class="
          replyText.length > MAX_BODY ? 'border-n-ruby-8' : 'border-n-weak'
        "
        @input="emit('field', 'text', $event.target.value)"
      />
      <p
        v-if="isRoot && greetingTextEmpty"
        class="mt-1.5 flex items-center gap-1.5 text-[11.5px] font-semibold text-n-amber-11"
      >
        <span class="i-lucide-triangle-alert size-3.5" />
        {{ t('AUTO_REPLY_BOT_SETTINGS.ENTRY.GREETING_TEXT_REQUIRED') }}
      </p>
    </div>

    <!-- night text (optional, shown together with day) -->
    <div class="mb-4 rounded-xl border border-n-weak/70 bg-n-alpha-1 p-3">
      <div class="mb-1.5 flex items-center justify-between">
        <label
          class="flex items-center gap-1.5 text-[12.5px] font-bold text-n-slate-12"
        >
          <span class="i-lucide-moon size-3.5 text-n-slate-11" />
          {{
            isRoot
              ? t('AUTO_REPLY_BOT_SETTINGS.EDITOR.GREETING_NIGHT_LABEL')
              : t('AUTO_REPLY_BOT_SETTINGS.EDITOR.NIGHT_BODY_LABEL')
          }}
        </label>
        <span
          dir="ltr"
          class="text-[11px] font-semibold"
          :class="
            offHoursText.length > MAX_BODY
              ? 'text-n-ruby-11'
              : 'text-n-slate-10'
          "
        >
          {{ `${offHoursText.length}/${MAX_BODY}` }}
        </span>
      </div>
      <p class="mb-2 text-[11.5px] font-medium text-n-slate-10">
        {{ t('AUTO_REPLY_BOT_SETTINGS.EDITOR.NIGHT_OPTIONAL_HINT') }}
      </p>
      <textarea
        :value="screen.offHours"
        :rows="isRoot ? 2 : 4"
        :placeholder="
          isRoot
            ? t('AUTO_REPLY_BOT_SETTINGS.EDITOR.OFF_HOURS_PREFIX_PLACEHOLDER')
            : t('AUTO_REPLY_BOT_SETTINGS.EDITOR.OFF_HOURS_REPLY_PLACEHOLDER')
        "
        class="w-full resize-y rounded-xl border bg-n-alpha-black1 px-3 py-2.5 text-[13.5px] leading-relaxed text-n-slate-12 outline-none transition-colors focus:border-n-brand"
        :class="
          offHoursText.length > MAX_BODY ? 'border-n-ruby-8' : 'border-n-weak'
        "
        @input="emit('field', 'offHours', $event.target.value)"
      />
    </div>

    <!-- buttons -->
    <div class="mt-1">
      <div class="mb-1 flex items-center justify-between">
        <h3 class="text-[13px] font-extrabold text-n-slate-12">
          {{
            isRoot
              ? t('AUTO_REPLY_BOT_SETTINGS.ENTRY.TITLE')
              : t('AUTO_REPLY_BOT_SETTINGS.EDITOR.BUTTONS_TITLE')
          }}
        </h3>
        <span class="text-[11.5px] font-semibold text-n-slate-10" dir="ltr">
          {{ `${buttons.length}/${MAX_BUTTONS}` }}
        </span>
      </div>
      <p class="mb-2.5 text-[11.5px] font-medium text-n-slate-10">
        {{
          isRoot
            ? t('AUTO_REPLY_BOT_SETTINGS.ENTRY.SUB')
            : t('AUTO_REPLY_BOT_SETTINGS.EDITOR.BUTTONS_SAME_DAY_NIGHT')
        }}
      </p>

      <!-- greeting: red callout when there is no way in -->
      <div
        v-if="isRoot && entryButtonsEmpty && hasReplyScreens"
        class="mb-2.5 flex items-start gap-2.5 rounded-xl border border-n-ruby-6 bg-n-ruby-2 px-3 py-2.5 text-[12px] font-bold text-n-ruby-11"
      >
        <span class="i-lucide-octagon-alert size-4 shrink-0" />
        <span>{{ t('AUTO_REPLY_BOT_SETTINGS.ENTRY.EMPTY_CALLOUT') }}</span>
      </div>

      <div
        v-for="(btn, i) in buttons"
        :key="i"
        class="mb-2.5 flex items-stretch gap-2.5"
      >
        <div
          class="grid w-[30px] shrink-0 place-items-center rounded-[10px] bg-n-brand/10 text-xs font-extrabold text-n-brand"
          dir="ltr"
        >
          {{ i + 1 }}
        </div>
        <div
          class="flex-1 rounded-[13px] border border-n-weak bg-n-solid-1 p-2.5"
        >
          <div class="flex items-center gap-2">
            <input
              :value="btn.label"
              type="text"
              :maxlength="MAX_TITLE"
              :readonly="Boolean(btn.target)"
              :placeholder="
                t('AUTO_REPLY_BOT_SETTINGS.EDITOR.BTN_LABEL_PLACEHOLDER')
              "
              class="w-full rounded-[9px] border border-n-weak px-3 py-2 text-sm font-semibold outline-none transition-colors focus:border-n-brand"
              :class="
                btn.target
                  ? 'cursor-default bg-n-alpha-2 text-n-slate-10'
                  : 'bg-n-alpha-black1 text-n-slate-12'
              "
              @input="emit('btnLabel', i, $event.target.value)"
            />
            <button
              type="button"
              class="grid size-7 shrink-0 place-items-center self-center rounded-lg text-n-slate-10 transition-colors hover:bg-n-ruby-3 hover:text-n-ruby-11"
              @click="emit('btnRemove', i)"
            >
              <span class="i-lucide-x size-4" />
            </button>
          </div>
          <p
            v-if="btn.target"
            class="mt-1.5 flex items-center gap-1.5 text-[11px] font-medium text-n-slate-10"
          >
            <span class="i-lucide-lock size-3 shrink-0" />
            {{ t('AUTO_REPLY_BOT_SETTINGS.EDITOR.LINKED_LABEL_LOCK') }}
          </p>
          <div
            class="mt-2.5 flex flex-wrap items-center gap-2 border-t border-dashed border-n-weak pt-2.5"
          >
            <span
              class="flex shrink-0 items-center gap-1.5 whitespace-nowrap text-[11.5px] font-bold text-n-slate-10"
            >
              <span class="i-lucide-corner-down-left size-3.5" />
              {{ t('AUTO_REPLY_BOT_SETTINGS.EDITOR.ON_TAP') }}
            </span>
            <TargetDropdown
              :model-value="btn.target"
              :options="targetOptions"
              @update:model-value="emit('btnTarget', i, $event)"
              @create-new="emit('btnNew', i)"
            />
            <span
              class="whitespace-nowrap rounded-full px-2.5 py-1 text-[10.5px] font-extrabold"
              :class="
                btn.target
                  ? 'bg-n-brand/10 text-n-brand'
                  : 'bg-n-alpha-2 text-n-slate-10'
              "
            >
              {{
                btn.target
                  ? t('AUTO_REPLY_BOT_SETTINGS.EDITOR.BTN_OPENS')
                  : t('AUTO_REPLY_BOT_SETTINGS.EDITOR.BTN_ENDS')
              }}
            </span>
          </div>
        </div>
      </div>

      <button
        v-if="buttons.length < MAX_BUTTONS"
        type="button"
        class="flex w-full items-center justify-center gap-2 rounded-[13px] border border-dashed border-n-weak bg-n-alpha-black1 py-3 text-[13px] font-bold text-n-slate-11 transition-colors hover:border-n-brand hover:bg-n-brand/5 hover:text-n-brand"
        @click="emit('btnAdd')"
      >
        <span class="i-lucide-plus size-4" />
        {{
          isRoot
            ? t('AUTO_REPLY_BOT_SETTINGS.ENTRY.ADD_ENTRY_BUTTON')
            : t('AUTO_REPLY_BOT_SETTINGS.EDITOR.ADD_BUTTON')
        }}
      </button>
    </div>
  </div>
</template>
